import React, { useMemo, useState } from "react";
import { Modal } from "antd";
import { IconCalendar, IconChevronRight, IconHistory, IconSearch } from "./icons";
import {
  MODE_LABELS,
  formatDateTime,
  initialsOf,
  isPast,
  isScheduled,
  statusLabel,
  summaryOf,
} from "./policyUtils";
import styles from "./BillingPolicies.module.css";

const STATUS_CLASS = {
  "In force": styles.tagOk,
  Scheduled: styles.tagWait,
  Cancelled: styles.tagGone,
  Replaced: styles.tagGone,
};

const StatusTag = ({ policy }) => {
  const label = statusLabel(policy);
  return (
    <span className={`${styles.tag} ${styles.tagDot} ${STATUS_CLASS[label] || styles.tagOff}`}>
      {label}
    </span>
  );
};

/* Policy history folds open under its facility, so the versions behind a
   policy are read in place rather than on another screen. */
const History = ({ group, onCancel }) => {
  const versions = group.versions.slice().reverse();
  return (
    <div className={styles.hist}>
      <div className={styles.histBox}>
        <div className={styles.histT}>
          <IconHistory size={15} />
          Policy history
          <span className={styles.histN}>
            · {versions.length} version{versions.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className={styles.histScroll}>
          <table className={styles.tbl}>
            <thead>
              <tr>
                <th>Version</th>
                <th>Mode</th>
                <th>Effective from</th>
                <th>Status</th>
                <th className={styles.hideS}>Created by</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {versions.length ? (
                versions.map((version) => (
                  <tr key={version.id ?? version.version} className={isPast(version) ? styles.dim : ""}>
                    <td>
                      <span className={styles.vchip}>v{version.version}</span>
                    </td>
                    <td>{MODE_LABELS[version.billing_mode] || version.billing_mode}</td>
                    <td className={styles.tnum}>{formatDateTime(version.effective_from)}</td>
                    <td>
                      <StatusTag policy={version} />
                    </td>
                    <td className={styles.hideS}>
                      {version.created_by?.name || "System"}
                      {version.created_at && (
                        <div className={styles.hint}>{formatDateTime(version.created_at)}</div>
                      )}
                    </td>
                    <td>
                      <div className={styles.rowActs}>
                        {isScheduled(version) && (
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
                            onClick={() => onCancel(group, version)}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className={styles.empty}>
                    No versions recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const FacilityRow = ({ group, isOpen, flashing, onToggle, onConfigure, onCancel }) => {
  const { facility, current, scheduled } = group;
  const classNames = [styles.fItem, isOpen ? styles.open : "", flashing ? styles.flash : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classNames}>
      <div
        className={styles.fRow}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onClick={() => onToggle(group.id)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle(group.id);
          }
        }}
      >
        <span className={styles.chev} title="History">
          <IconChevronRight size={16} width={2.4} />
        </span>

        <div className={styles.fName}>
          <span className={styles.fAv}>{initialsOf(facility.name)}</span>
          <div style={{ minWidth: 0 }}>
            <b>{facility.name}</b>
            <small>{facility.city || facility.timezone}</small>
          </div>
        </div>

        <div className={styles.pol}>
          {current ? (
            <>
              <b>
                {MODE_LABELS[current.billing_mode] || current.billing_mode}
                <span className={styles.vchip}>v{current.version}</span>
              </b>
              <small title={summaryOf(current)}>{summaryOf(current)}</small>
            </>
          ) : (
            <span className={styles.none}>No policy yet</span>
          )}
        </div>

        <div className={styles.next}>
          {scheduled ? (
            <>
              <b>
                {MODE_LABELS[scheduled.billing_mode] || scheduled.billing_mode}
                <span className={styles.vchip}>v{scheduled.version}</span>
              </b>
              <small>
                <IconCalendar size={12} />
                from {formatDateTime(scheduled.effective_from)}
              </small>
            </>
          ) : (
            <span className={styles.none}>Nothing scheduled</span>
          )}
        </div>

        <div className={styles.fAct} onClick={(event) => event.stopPropagation()}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
            onClick={() => onConfigure(group)}
          >
            Configure
          </button>
        </div>
      </div>

      {isOpen && <History group={group} onCancel={onCancel} />}
    </div>
  );
};

const PolicyList = ({ groups, flashFacilityId, onConfigure, onCancel }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(() =>
    flashFacilityId ? { [flashFacilityId]: true } : {}
  );
  const [confirming, setConfirming] = useState(null);

  const toggle = (id) => setOpen((prev) => ({ ...prev, [id]: !prev[id] }));

  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return groups;
    return groups.filter((group) =>
      `${group.facility.name} ${group.facility.city}`.toLowerCase().includes(term)
    );
  }, [groups, query]);

  const confirmCancel = async () => {
    const pending = confirming;
    setConfirming(null);
    await onCancel(pending.group, pending.version);
  };

  return (
    <>
      <div className={styles.lHead}>
        <div>
          <h3>Inpatient billing policies</h3>
          <p>
            How bed and IP package days are counted at each facility. A change is scheduled ahead
            and never reprices an admission already in progress.
          </p>
        </div>
        <span className={styles.sp} />
        {groups.length > 1 && (
          <div className={styles.srch}>
            <IconSearch size={16} />
            <input
              className={styles.fi}
              value={query}
              placeholder="Search facility"
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        )}
      </div>

      <div className={`${styles.card} ${styles.fList}`}>
        <div className={styles.fHdr}>
          <div />
          <div>Facility</div>
          <div>Policy in force</div>
          <div>Next change</div>
          <div style={{ textAlign: "right" }}>Action</div>
        </div>
        {rows.length ? (
          rows.map((group) => (
            <FacilityRow
              key={group.id}
              group={group}
              isOpen={!!open[group.id]}
              flashing={flashFacilityId === group.id}
              onToggle={toggle}
              onConfigure={onConfigure}
              onCancel={(g, version) => setConfirming({ group: g, version })}
            />
          ))
        ) : (
          <div className={styles.empty}>
            {groups.length ? `No facility matches “${query}”.` : "No facilities to show."}
          </div>
        )}
      </div>

      <Modal
        open={!!confirming}
        centered
        title={`Cancel scheduled v${confirming?.version?.version}?`}
        okText={`Cancel v${confirming?.version?.version}`}
        cancelText="Keep it"
        okButtonProps={{ danger: true }}
        onOk={confirmCancel}
        onCancel={() => setConfirming(null)}
      >
        {confirming?.group?.facility?.name} keeps the policy in force now. The cancelled version
        stays in its history.
      </Modal>
    </>
  );
};

export default PolicyList;
